import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, MapPin, ArrowRight, Layers } from "lucide-react";
import { api, API } from "@/lib/apiClient";
import JobListings from "@/components/JobListings";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3ODc1NjczNDd8MA&ixlib=rb-4.1.0&q=85";

export default function Careers() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/tenant/${slug}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-[#86868B]">Company not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#86868B]">Loading…</div>;

  const t = data.tenant;
  const accent = t.accent_color || "#007AFF";
  const heroUrl = t.has_cover ? `${API}/public/tenant/${slug}/cover` : DEFAULT_HERO;
  const logoUrl = t.has_logo ? `${API}/public/tenant/${slug}/logo` : null;

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Hero */}
      <div className="relative h-[440px] overflow-hidden">
        <img src={heroUrl} alt="Team" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-[#F5F5F7]" />
        <div className="relative max-w-5xl mx-auto px-6 h-full flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            {logoUrl ? (
              <div className="w-16 h-16 rounded-2xl bg-white p-2 shadow-lg mb-4 overflow-hidden">
                <img src={logoUrl} alt={t.name} className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-3 py-1 text-white text-xs font-semibold mb-4">
                <Layers size={13} /> {t.name}
              </div>
            )}
            <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tighter text-white leading-none">
              Careers at {t.name}
            </h1>
            <p className="text-white/85 mt-4 text-lg max-w-xl font-medium">
              {data.jobs.length} open role{data.jobs.length !== 1 ? "s" : ""}. Apply in minutes — we'll get back to you fast.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-24 -mt-10 relative">
        {/* About */}
        {t.about && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-black/5 p-7 mb-6" data-testid="careers-about">
            <h2 className="font-bold text-lg tracking-tight mb-2" style={{ color: accent }}>About {t.name}</h2>
            <p className="text-[#1D1D1F] leading-relaxed whitespace-pre-wrap">{t.about}</p>
          </motion.div>
        )}

        {data.jobs.length === 0 ? (
          <div className="bg-white rounded-3xl border border-black/5 p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
            <Briefcase size={40} className="mx-auto text-[#C7C7CC] mb-3" strokeWidth={1.5} />
            <p className="font-semibold text-lg">No open positions right now</p>
            <p className="text-[#86868B]">Check back soon.</p>
          </div>
        ) : (
          <JobListings jobs={data.jobs} accent={accent} layout={t.layout || "card"} onSelect={(job) => navigate(`/apply/${job.id}`)} />
        )}
        <p className="text-center text-xs text-[#A1A1A6] mt-12">Powered by HireScope</p>
      </div>
    </div>
  );
}
